export const CHECKOUT_PREWARM_TTL_MS = 5 * 60 * 1000;

export type TransactionSignatureParams = {
  discountId?: string;
  quantity?: number;
  toolType?: string;
  paywallType?: string;
};

export const buildTransactionSignature = ({
  discountId,
  quantity,
}: TransactionSignatureParams): string => `${discountId ?? ''}:${quantity ?? ''}`;

type TransactionPromiseEntry = {
  expiresAt: number;
  promise: Promise<string>;
  signature: string;
};

const pendingTransactions = new Map<string, TransactionPromiseEntry>();

const getValidEntry = (priceId: string) => {
  const entry = pendingTransactions.get(priceId);

  if (!entry) {
    return undefined;
  }

  if (entry.expiresAt <= Date.now()) {
    pendingTransactions.delete(priceId);
    return undefined;
  }

  return entry;
};

export const transactionPromiseStore = {
  get(priceId: string, signature: string): Promise<string> | undefined {
    const entry = getValidEntry(priceId);
    return entry?.signature === signature ? entry.promise : undefined;
  },

  set(
    priceId: string,
    promise: Promise<string>,
    signature: string,
    ttlMs = CHECKOUT_PREWARM_TTL_MS
  ): void {
    pendingTransactions.set(priceId, {
      expiresAt: Date.now() + ttlMs,
      promise,
      signature,
    });
  },

  isCurrent(priceId: string, promise: Promise<string>): boolean {
    return getValidEntry(priceId)?.promise === promise;
  },

  getExpiresAt(priceId: string, promise: Promise<string>): number | undefined {
    const entry = getValidEntry(priceId);
    return entry?.promise === promise ? entry.expiresAt : undefined;
  },

  delete(priceId: string, promise?: Promise<string>): void {
    if (promise && pendingTransactions.get(priceId)?.promise !== promise) {
      return;
    }

    pendingTransactions.delete(priceId);
  },

  deleteOtherPrices(priceId: string): void {
    pendingTransactions.forEach((_, storedPriceId) => {
      if (storedPriceId !== priceId) {
        pendingTransactions.delete(storedPriceId);
      }
    });
  },

  clear(): void {
    pendingTransactions.clear();
  },
};
